import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { getPriceClassesUseCase } from '../use-cases/get-price-classes.use-case';
import { getPriceBackgroundAndColorUseCase } from '../use-cases/get-price-styles.use-cases';
import { calculatePricesUseCase } from '../use-cases/get-prices.use-cases';

import {
    configCrmActive$,
    configEnableTriplePrice$,
    configEnablePercent$,
    configTriplePricePercent$,
    configTypePercent$,
    vipSalesDateRange$,
    textsDiscountsPrewarmVea$,
    configTriplePriceRemark$,
    blackFridayDateRange$,
} from './product-prices.selectors';

import { EcommerceContext } from '@core/domain/models/context.models';
import { getCurrentDate, getCountryCode, getPage, getDebugTextDiscount, getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { getUserEventIsSales$, getPeriod } from '@features/user-events';
import { formatPriceWithCurrencySymbol } from '@frontend/common/currency';
import {
    ProductMapPrices,
    SalesPeriod,
    PricesClasses,
    ClassPrice,
    PricesType,
    Discount,
    ProductPricesDetail,
    PriceStyles,
} from '@shared/prices/models/product-prices.model';
import { selectUserIsVip } from '@shared/user/store/user.selectors';

@Injectable()
export class ProductPriceService {
    constructor(
        private readonly store: Store,
        private readonly translate: TranslateService,
    ) {}

    public getFormatedPrice(orderPrice: number, context: EcommerceContext): string {
        return formatPriceWithCurrencySymbol(orderPrice, context.store.details.currencyInfo);
    }

    public setPriceInfo(status: ProductPublicModels.PublicProductState): ProductPublicModels.PublicProductPrice {
        if (status) {
            let color: ProductPublicModels.PublicProductColor;
            let size: ProductPublicModels.PublicProductSize;
            let price: ProductPublicModels.PublicProductPrice;
            if (status?.selectedSize) {
                size = status.selectedSize;
                price = size.priceInfo;
                color = status.selectedColor;
            }
            if (status.selectedColor && !status.selectedSize) {
                color = status.selectedColor;
            }
            if (color?.sizes && color?.sizes?.length > 0) {
                size = color.sizes[0];
                price = size.priceInfo;
            }
            return price;
        }
    }

    public getPrices(product: ProductPublicModels.PublicProduct): Observable<ProductMapPrices> {
        return combineLatest([
            this.store.select(getPeriod).pipe(filter(value => !!value)),
            getUserEventIsSales$(this.store),
            this.store.select(configEnableTriplePrice$).pipe(first()),
            this.store.select(configTriplePricePercent$).pipe(first()),
            this.store.select(configEnablePercent$).pipe(first()),
            this.store.select(configTypePercent$).pipe(first()),
        ]).pipe(
            map(([
                period,
                showPrivateSale,
                configEnableTriplePrice,
                configTriplePricePercent,
                configShowPercent,
                configTypePercent,
            ]) => {
                return calculatePricesUseCase(
                    product,
                    period,
                    showPrivateSale,
                    configEnableTriplePrice,
                    configTriplePricePercent,
                    configShowPercent,
                    configTypePercent,
                );
            })
        );
    }

    public getClassPrices(
        mapPrices: ProductMapPrices,
        hasPromotion = false,
        skipCheckPage = false
    ): Observable<PricesClasses> {
        return combineLatest([
            this.store.select(configCrmActive$).pipe(first(), map(active => JSON.parse(active))),
            this.store.select(configTriplePriceRemark$).pipe(first()),
            this.store.select(textsDiscountsPrewarmVea$).pipe(first()),
            this.store.select(getPage).pipe(first()),
            this.store.select(selectUserIsVip),
            this.store.select(getCountryCode).pipe(first()),
            this.store.select(vipSalesDateRange$),
            this.store.select(getCurrentDate).pipe(first()),
            this.store.select(getCmsConfigValue('Enable_rebajas_sin_corte')).pipe(first()),
        ]).pipe(
            map(([
                crmActive,
                triplePriceRemark,
                textsDiscountsPrewarmVea,
                page,
                userIsVip,
                countryCode,
                vipSalesDateRange,
                currentDate,
                enableRebajasSinCorte,
            ]) => {
                return getPriceClassesUseCase(
                    mapPrices,
                    hasPromotion,
                    crmActive,
                    triplePriceRemark,
                    textsDiscountsPrewarmVea,
                    page,
                    userIsVip,
                    countryCode,
                    vipSalesDateRange,
                    currentDate,
                    enableRebajasSinCorte,
                    skipCheckPage,
                );
            })
        );
    }

    public getPriceStyles(mapPrices: ProductMapPrices, hasPromotion = false): Observable<PriceStyles> {
        return combineLatest([
            this.store.select(getCountryCode).pipe(first()),
            this.store.select(getCurrentDate).pipe(first()),
            this.store.select(getCmsConfigValue('events_colors')).pipe(
                filter((eventsColors) => {
                    // Check if the CMS config has the required properties
                    return (
                        eventsColors?.prices?.base
                        && eventsColors?.prices?.event
                        && eventsColors?.prices?.base?.background_color
                        && eventsColors?.prices?.base?.text_color
                        && eventsColors?.prices?.event?.background_color
                        && eventsColors?.prices?.event?.text_color
                    );
                }),
                first()
            ),
            this.store.select(getPeriod),
            this.store.select(blackFridayDateRange$),
            this.store.select(vipSalesDateRange$),
            this.store.select(configTriplePriceRemark$).pipe(first()),
            this.store.select(selectUserIsVip),
            this.store.select(getCmsConfigValue('Enable_rebajas_sin_corte')).pipe(first()),
        ]).pipe(
            map(([
                countryCode,
                currentDate,
                eventsColors,
                period,
                blackFridayDateRange,
                vipSalesDateRange,
                configTriplePriceRemark,
                isVip,
                enableRebajasSinCorte,
            ]) => {
                return getPriceBackgroundAndColorUseCase(
                    mapPrices,
                    countryCode,
                    currentDate,
                    eventsColors?.prices,
                    period,
                    blackFridayDateRange,
                    vipSalesDateRange,
                    configTriplePriceRemark,
                    hasPromotion,
                    isVip,
                    enableRebajasSinCorte
                );
            })
        );
    }

    public getDiscountText(productPrices: ProductMapPrices) {
        return combineLatest([
            this.store.select(getPeriod).pipe(filter(value => !!value)),
            this.store.select(selectUserIsVip),
            this.store.select(getCmsConfigValue('vip_config')).pipe(map(vipConfig => vipConfig !== 'ALL')),
            this.store.select(getPage),
            this.store.select(configCrmActive$).pipe(first()),
            this.store.select(textsDiscountsPrewarmVea$).pipe(first()),
            this.store.select(getDebugTextDiscount).pipe(first()),
            this.store.select(getCmsConfigValue('prewarm_date_hour_keys')),
        ]).pipe(
            map(([period, userIsVip, vipCTA, page, crmActive, textsDiscountsPrewarmVea, debugTextDiscount, config]:
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
            [string, boolean, boolean, string, boolean, any, boolean, any]): Discount => {
                const cta: boolean = !userIsVip && vipCTA;
                const price: ProductPricesDetail = productPrices.price;
                const categoryPage = page === 'ItxCategoryPage';

                const linkClass = cta ? 'link-line' : '';

                const discountTypeClass = crmActive ? ClassPrice.textPromotionFeel : ClassPrice.textPromotionNwl;
                let classPromotion = ClassPrice.textPromotion;

                if (productPrices.oldPrice.type !== PricesType.OLDPRICE) {
                    classPromotion = `${discountTypeClass} ${linkClass}`;
                }

                let textPromotion = false;
                if (period === SalesPeriod.VEA || period === SalesPeriod.PREWARM) {
                    textPromotion = price.type !== PricesType.PRICE;
                }

                if (debugTextDiscount) {
                    textPromotion = period === SalesPeriod.VEA || period === SalesPeriod.PREWARM;
                    classPromotion = cta ? `${discountTypeClass} ${linkClass}` : ClassPrice.textPromotion;
                }

                const promotionGrids = textPromotion && categoryPage && textsDiscountsPrewarmVea.grids;
                const promotionProduct = textPromotion && textsDiscountsPrewarmVea.product_view;

                const translateKey = this.translate.instant(this.getKeyDiscount(period, userIsVip, categoryPage));
                // biome-ignore lint/nursery/useTopLevelRegex: <explanation>
                const translateHaveConfig = /\[\[date1]]|\[\[hour1]]|\[\[date2]]|\[\[hour2]]/.test(translateKey);

                let key = this.getKeyDiscount(period, userIsVip, categoryPage);

                if (translateHaveConfig) {
                    const keys = Object.keys(config).join('|');
                    const regex = new RegExp(keys, 'g');

                    const modifiedString = translateKey.replace(regex, matched => config[matched]);
                    key = modifiedString.replaceAll('[[', '').replaceAll(']]', '');
                }

                return {
                    cta,
                    key: key,
                    class: classPromotion,
                    promotionInfo: promotionProduct || promotionGrids,
                };
            }),
        );
    }

    private getKeyDiscount(period: string, userIsVip: boolean, categoryPage: boolean): string {
        const todayOrTomorrow = period === SalesPeriod.PREWARM ? 'tomorrow' : 'today';
        const vipString = userIsVip ? 'vip' : 'novip';
        const hasWeb = period === SalesPeriod.PREWARM ? '' : '.web';
        const categoryPlace = `dev.product.prewarming.${todayOrTomorrow}.${vipString}.message`;
        const productPlace = `dev.product.detail.prewarming.${todayOrTomorrow}.${vipString}.message${hasWeb}`;
        return categoryPage ? categoryPlace : productPlace;
    }
}
